import { useMemo } from 'react';

const WEEKDAY_SLOTS = ['07:00','07:30','08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30'];
const SATURDAY_SLOTS: string[] = [];

interface Props {
  date: string;
  occupiedMap: Record<string, string[]>;
  selectedTime: string | null;
  onSelect: (time: string) => void;
  onBack: () => void;
}

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return `${d.getDate()} de ${MONTHS[d.getMonth()]} de ${d.getFullYear()}`;
}

type SlotStatus = 'available' | 'occupied' | 'past' | 'selected';

const BookingSlots = ({ date, occupiedMap, selectedTime, onSelect, onBack }: Props) => {
  const slots = useMemo(() => {
    const dow = new Date(date + 'T12:00:00').getDay();
    return dow === 6 ? SATURDAY_SLOTS : WEEKDAY_SLOTS;
  }, [date]);

  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const isToday = date === todayStr;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const occupied = occupiedMap[date] ?? [];

  const getStatus = (slot: string): SlotStatus => {
    if (slot === selectedTime) return 'selected';
    if (occupied.includes(slot)) return 'occupied';
    if (isToday) {
      const [h, m] = slot.split(':').map(Number);
      if (h * 60 + m <= nowMinutes + 60) return 'past';
    }
    return 'available';
  };

  const morning = slots.filter((s) => parseInt(s) < 15);
  const afternoon = slots.filter((s) => parseInt(s) >= 15);

  const renderSlot = (slot: string) => {
    const status = getStatus(slot);
    const cfg: Record<SlotStatus, string> = {
      selected: 'bg-yellow-400 border-yellow-400 text-black cursor-pointer',
      occupied: 'bg-red-500/10 border-red-500/30 text-red-400/60 cursor-not-allowed',
      past: 'bg-zinc-900/40 border-zinc-800 text-zinc-700 cursor-not-allowed line-through',
      available: 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-yellow-400/50 hover:bg-yellow-400/5 cursor-pointer',
    };
    return (
      <button
        key={slot}
        disabled={status === 'occupied' || status === 'past'}
        onClick={() => (status === 'available' || status === 'selected') && onSelect(slot)}
        className={`relative border px-2 py-3 text-center transition-all ${cfg[status]}`}
      >
        <span className="text-xs font-bold block">{slot}</span>
        {status === 'occupied' && <span className="text-[9px] block mt-0.5 font-bold uppercase tracking-wider">Ocupada</span>}
        {status === 'past' && <span className="text-[9px] block mt-0.5 uppercase tracking-wider">Pasada</span>}
      </button>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 cursor-pointer transition-colors">
          <i className="ri-arrow-left-line" />
        </button>
        <div>
          <p className="text-white font-black text-sm">{formatDate(date)}</p>
          <p className="text-zinc-500 text-xs">Selecciona una hora disponible</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
            <i className="ri-sun-line text-yellow-400/50" /> Mañana
          </p>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {morning.map(renderSlot)}
          </div>
        </div>

        {afternoon.length > 0 && (
          <div>
            <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
              <i className="ri-sun-foggy-line text-yellow-400/50" /> Tarde
            </p>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {afternoon.map(renderSlot)}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-zinc-800">
        {[
          { color: 'bg-yellow-400', label: 'Seleccionada' },
          { color: 'bg-zinc-900 border border-zinc-700', label: 'Disponible' },
          { color: 'bg-red-500/10 border border-red-500/30', label: 'Ocupada' },
          { color: 'bg-zinc-900/40 border border-zinc-800 opacity-50', label: 'Pasada' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 ${color}`} />
            <span className="text-zinc-600 text-[10px]">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookingSlots;
